<script setup lang="ts">
    import InputDecorator, {InputDecoratorProps} from './InputDecorator.vue';
    import Datepicker from '@vuepic/vue-datepicker';
    import '@vuepic/vue-datepicker/dist/main.css';
    import { useI18n } from 'vue-i18n';
    import useCurrentLocale from '../../Composables/current-locale';
    import { computed } from 'vue';
    import { DateDisplayMode } from '../Form/types';
    import { Theme, useTheme } from '../../Composables/theme';

    const props = withDefaults(defineProps<{
        id: string;
        value: Date | null;
        minDate?: Date;
        maxDate?: Date;
        minYear?: number;
        maxYear?: number;
        displayMode?: DateDisplayMode;
        disabled?: boolean;
        autoApply?: boolean;
        clearable?: boolean;
    } & InputDecoratorProps>(), {
        disabled: false,
        autoApply: false,
        displayMode: DateDisplayMode.NotSelected,
        clearable: false
    });

    const emit = defineEmits(['update', 'blur']);

    const { t } = useI18n();
    const { currentLocale, isoLocale, weekInfo } = useCurrentLocale();
    const { theme } = useTheme();

    const maxYear = computed(() => {
        return props.maxYear || props.maxDate?.getFullYear() || new Date().getFullYear();
    });

    const minYear = computed(() => {
        return props.minYear || props.minDate?.getFullYear() || new Date().getFullYear() - 100;
    });

    const value = computed({
        get: () => props.value,
        set: (value: Date | null) => emit('update', value)
    });

    const formatDate = (date: Date) => {
        const formattedDate = date.toLocaleDateString(
            isoLocale.value,
            { year: 'numeric', month: 'short', day: '2-digit' }
        );
        if (props.displayMode === DateDisplayMode.Age || props.displayMode === DateDisplayMode.DateAndAge) {
            const age = new Date().getFullYear() - date.getFullYear();
            const formattedAge =  `${age} ${t('form.date.years_old')}`;

            if (props.displayMode === DateDisplayMode.Age) {
                return formattedAge;
            }

            return `${formattedDate} (${formattedAge})`;
        }
        return formattedDate;
    };

    const weekStart = computed(() => (weekInfo.value.firstDay === 7) ? 0 : weekInfo.value.firstDay);

</script>
<template>
    <InputDecorator
        :for-id="id"
        :label="label"
        :help="help"
        :help-position="helpPosition"
        :errors="errors"
        :required="required"
    >
        <Datepicker
            v-model="value"
            :class="theme"
            :ui="{menu: theme || Theme.Yapla}"
            :format="formatDate"
            :enable-time-picker="false"
            :locale="currentLocale"
            :week-start="weekStart"
            :select-text="t('form.date.select')"
            :cancel-text="t('form.date.cancel')"
            :placeholder="t('form.date.placeholder')"
            :clearable="clearable"
            :disabled="disabled"
            :auto-apply="autoApply"
            :year-range="[minYear, maxYear]"
            :action-row="{ showPreview: false }"
            :teleport="true"
            :max-date="maxDate"
            :min-date="minDate"
            :start-date="maxDate"
            @blur="emit('blur')"
        />
    </InputDecorator>
</template>
<style lang="scss">
@import '@Common/Styles/datepicker';
</style>